/**
 * Epic 9 — web hook for the Doctrine Requirements Engine.
 *
 * Fetches approved requirements from the API on mount and resolves a DutyConfig
 * tailored to the current incident context (state, agencies, incident type).
 * Also returns the raw resolved params map so the insight engine can surface
 * live provenance-aware citations (e.g. the BA-shortfall card).
 * Falls back to DEFAULT_CONFIG-backed loadConfig() if the API is unavailable.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  loadConfig,
  resolveConfig,
  resolveRequirements,
  activeIncidentTypes,
  type DutyConfig,
  type DoctrineRequirement,
  type RequirementQuery,
  type RequirementParameterKey,
  type ResolvedParam,
  type Incident,
} from '@duty/shared';

function requirementQueryForIncident(incident: Incident, role?: string): RequirementQuery {
  return {
    state: 'NSW',
    agencies: [incident.leadAgency, ...incident.supportingAgencies],
    incidentTypes: activeIncidentTypes(incident),
    role,
  };
}

export interface ResolvedDoctrineState {
  config: DutyConfig;
  resolvedParams: Map<RequirementParameterKey, ResolvedParam>;
}

/**
 * Returns the resolved DutyConfig and the raw resolved params map for the
 * current incident context. Both are derived deterministically from the
 * fetched requirements corpus; the API being unavailable produces identical
 * behaviour to loadConfig() with an empty params map.
 */
export function useResolvedConfig(
  incident: Incident | null,
  role?: string,
): ResolvedDoctrineState {
  const [requirements, setRequirements] = useState<DoctrineRequirement[]>([]);

  useEffect(() => {
    let active = true;
    fetch('/api/requirements')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { requirements?: DoctrineRequirement[] } | null) => {
        if (active && data?.requirements && data.requirements.length > 0) {
          setRequirements(data.requirements);
        }
      })
      .catch(() => {
        // Silent: fall back to DEFAULT_CONFIG
      });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(() => {
    if (!incident || requirements.length === 0) {
      return { config: loadConfig(), resolvedParams: new Map() };
    }
    const query = requirementQueryForIncident(incident, role);
    const { params } = resolveRequirements(requirements, query);
    return { config: resolveConfig(requirements, query), resolvedParams: params };
  }, [incident, requirements, role]);
}
